/**
 * Niet in het origineel: het invoerveld voor een wachtwoord met een knop Toon/Verberg plus de
 * regels voor een nieuw wachtwoord als lijst die meeloopt met het typen.
 */
import { useState, type Ref } from 'react';
import {
  Button,
  Field,
  Flex,
  Input,
  InputGroup,
  useFieldContext,
  VisuallyHidden,
  type InputProps,
} from '@chakra-ui/react';
import { Circle, CircleCheck, CircleX } from 'lucide-react';
import type { Regel, RegelStatus } from './hulp';

type InvoerProps = Omit<InputProps, 'type' | 'autoComplete' | 'ref'> & {
  autoComplete: 'current-password' | 'new-password';
  ref?: Ref<HTMLInputElement>;
};

/**
 * Een wachtwoordveld; hoort binnen een `Field.Root` met een `Field.Label`. De knop is een
 * echte knop, zodat hij ook met het toetsenbord werkt.
 */
export function WachtwoordInvoer({ ref, ...props }: InvoerProps) {
  const [zichtbaar, setZichtbaar] = useState(false);
  const veld = useFieldContext();
  return (
    <InputGroup
      endElement={
        <Button
          type="button"
          size="xs"
          variant="ghost"
          colorPalette="ameBlue"
          color="ameBlue.600"
          fontWeight="semibold"
          aria-label={zichtbaar ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
          aria-controls={veld?.ids.control}
          onClick={() => setZichtbaar((nu) => !nu)}
        >
          {zichtbaar ? 'Verberg' : 'Toon'}
        </Button>
      }
      endElementProps={{ pe: '1.5' }}
    >
      <Input
        ref={ref}
        size="lg"
        pe="20"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        {...props}
        type={zichtbaar ? 'text' : 'password'}
      />
    </InputGroup>
  );
}

const KLEUR: Record<RegelStatus, string> = {
  goed: 'green.700',
  open: 'gray.600',
  fout: 'red.600',
};

/** Voor een schermlezer, want het verschil zit verder alleen in het icoon en de kleur. */
const VOOR_SCHERMLEZER: Record<RegelStatus, string> = {
  goed: 'In orde: ',
  open: 'Nog open: ',
  fout: 'Niet in orde: ',
};

/**
 * De regels onder het veld, met een vinkje zodra een regel klopt. Het is de hulptekst van
 * het veld, dus een schermlezer leest hem voor bij het invoerveld.
 */
export function WachtwoordRegels({ regels }: { regels: Regel[] }) {
  return (
    <Field.HelperText asChild>
      <Flex as="ul" direction="column" gap="1" listStyleType="none" m="0" mt="1" p="0">
        {regels.map(({ tekst, status }) => (
          <Flex
            as="li"
            key={tekst}
            data-status={status}
            alignItems="flex-start"
            gap="2"
            textStyle="sm"
            color={KLEUR[status]}
          >
            <Flex as="span" aria-hidden flexShrink={0} h="5" alignItems="center">
              {status === 'goed' ? (
                <CircleCheck size="16" />
              ) : status === 'fout' ? (
                <CircleX size="16" />
              ) : (
                <Circle size="16" color="var(--chakra-colors-gray-400)" />
              )}
            </Flex>
            <span>
              <VisuallyHidden>{VOOR_SCHERMLEZER[status]}</VisuallyHidden>
              {tekst}
            </span>
          </Flex>
        ))}
      </Flex>
    </Field.HelperText>
  );
}
